import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getBills, payBill, getSafetyTasks, type BillRecord, type SafetyTask } from "@/mocks/sharedState";
import EnergyAnalysis from "./components/EnergyAnalysis";
import PurchaseAnalysis from "./components/PurchaseAnalysis";
import AICompareReport from "./components/AICompareReport";

type TabType = "home" | "balance" | "purchase" | "repair" | "safety" | "profile" | "install" | "news" | "energy" | "purchaseAnalysis" | "aiReport";

const mockUser = {
  name: "张建国",
  userId: "R001",
  address: "朝阳区望京街道1号楼3单元201",
  balance: 128.50,
  gasUsed: 256.8,
  meterNo: "IC-2024-001234",
  lastPurchase: "2026-03-28",
  nextSafety: "2026-06-15",
  phone: "138****5678",
  email: "zhang***@163.com",
  safetyScore: 92,
  installStatus: "已完成",
};

const purchaseAmounts = [50, 100, 200, 500];

const billRecords = [
  { id: "BILL-2026-0401", month: "2026年4月", amount: 89.34, gasUsed: 25.9, status: "已出账", dueDate: "2026-04-30", paid: false, unitPrice: 3.45 },
  { id: "BILL-2026-0301", month: "2026年3月", amount: 102.56, gasUsed: 29.7, status: "已缴费", dueDate: "2026-03-31", paid: true, unitPrice: 3.45 },
  { id: "BILL-2026-0201", month: "2026年2月", amount: 134.20, gasUsed: 38.9, status: "已缴费", dueDate: "2026-02-28", paid: true, unitPrice: 3.45 },
  { id: "BILL-2026-0101", month: "2026年1月", amount: 118.90, gasUsed: 34.5, status: "已缴费", dueDate: "2026-01-31", paid: true, unitPrice: 3.45 },
  { id: "BILL-2025-1201", month: "2025年12月", amount: 145.60, gasUsed: 42.2, status: "已缴费", dueDate: "2025-12-31", paid: true, unitPrice: 3.45 },
  { id: "BILL-2025-1101", month: "2025年11月", amount: 98.40, gasUsed: 28.5, status: "已缴费", dueDate: "2025-11-30", paid: true, unitPrice: 3.45 },
];

const payHistoryRecords = [
  { id: "PAY-20260328", date: "2026-03-28 14:22", amount: 200, method: "微信支付", status: "成功", type: "购气充值", icon: "ri-wechat-pay-line", iconColor: "text-green-500" },
  { id: "PAY-20260301", date: "2026-03-01 09:05", amount: 102.56, method: "银行代扣", status: "成功", type: "月度账单", icon: "ri-bank-line", iconColor: "text-gray-500" },
  { id: "PAY-20260201", date: "2026-02-01 08:30", amount: 134.20, method: "支付宝", status: "成功", type: "月度账单", icon: "ri-alipay-line", iconColor: "text-sky-500" },
  { id: "PAY-20260115", date: "2026-01-15 16:48", amount: 100, method: "微信支付", status: "成功", type: "购气充值", icon: "ri-wechat-pay-line", iconColor: "text-green-500" },
  { id: "PAY-20260101", date: "2026-01-01 08:00", amount: 118.90, method: "银行代扣", status: "成功", type: "月度账单", icon: "ri-bank-line", iconColor: "text-gray-500" },
  { id: "PAY-20251201", date: "2025-12-01 08:00", amount: 145.60, method: "银行代扣", status: "成功", type: "月度账单", icon: "ri-bank-line", iconColor: "text-gray-500" },
];

const installSteps = [
  { step: 1, label: "申请提交", status: "done", date: "2026-01-05", desc: "在线提交报装申请，资料审核通过" },
  { step: 2, label: "现场勘查", status: "done", date: "2026-01-10", desc: "工程师上门勘查，出具施工方案" },
  { step: 3, label: "合同签订", status: "done", date: "2026-01-15", desc: "签订供气合同，缴纳工程费用" },
  { step: 4, label: "施工安装", status: "done", date: "2026-01-22", desc: "专业团队完成管道安装" },
  { step: 5, label: "验收通气", status: "done", date: "2026-01-28", desc: "验收合格，正式通气使用" },
  { step: 6, label: "完成归档", status: "done", date: "2026-01-30", desc: "档案归档，用户信息录入系统" },
];

const newsItems = [
  { id: 1, type: "通知", title: "关于2026年天然气价格调整的通知", date: "2026-04-08", read: false, content: "根据国家发改委相关政策，自2026年5月1日起，居民用气价格调整为3.65元/m³，请广大用户知悉。" },
  { id: 2, type: "安全", title: "春季用气安全提示：请定期检查燃气设备", date: "2026-04-05", read: false, content: "春季气温回升，请注意检查燃气软管是否老化，保持厨房通风，如发现异味请立即关闭阀门并拨打服务热线。" },
  { id: 3, type: "活动", title: "老用户专属：绑定银行卡享代扣优惠", date: "2026-04-01", read: true, content: "即日起，绑定银行卡开通自动代扣的老用户，每月可享受5元话费补贴，活动截止2026年6月30日。" },
  { id: 4, type: "维护", title: "4月12日朝阳区管网维护停气公告", date: "2026-03-30", read: true, content: "因管网维护需要，2026年4月12日8:00-18:00，朝阳区望京街道部分区域将临时停气，请提前做好准备。" },
];

const safetyCheckItems = [
  { label: "燃气表", status: "正常", icon: "ri-dashboard-line", color: "text-green-600" },
  { label: "软管", status: "正常", icon: "ri-git-branch-line", color: "text-green-600" },
  { label: "阀门", status: "正常", icon: "ri-settings-3-line", color: "text-green-600" },
  { label: "报警器", status: "需更换", icon: "ri-alarm-warning-line", color: "text-amber-600" },
  { label: "通风", status: "良好", icon: "ri-refresh-line", color: "text-green-600" },
  { label: "灶具", status: "正常", icon: "ri-fire-line", color: "text-green-600" },
];

export default function MobilePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabType>("home");
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [paySuccess, setPaySuccess] = useState(false);
  const [repairType, setRepairType] = useState("");
  const [repairDesc, setRepairDesc] = useState("");
  const [repairSubmitted, setRepairSubmitted] = useState(false);
  const [safetyDate, setSafetyDate] = useState("");
  const [safetyTimeSlot, setSafetyTimeSlot] = useState("");
  const [safetySubmitted, setSafetySubmitted] = useState(false);
  const [balanceSubTab, setBalanceSubTab] = useState<"balance" | "bills" | "history" | "invoice">("balance");
  const [invoiceModal, setInvoiceModal] = useState<typeof billRecords[0] | null>(null);
  const [billDetail, setBillDetail] = useState<typeof billRecords[0] | null>(null);
  const [selectedNews, setSelectedNews] = useState<typeof newsItems[0] | null>(null);
  const [invoiceType, setInvoiceType] = useState("增值税普通发票");
  const [titleType, setTitleType] = useState("personal");
  const [invoiceEmail, setInvoiceEmail] = useState("");
  const [invoiceApplied, setInvoiceApplied] = useState(false);
  const [payMethod, setPayMethod] = useState("wechat");
  const [profileEdit, setProfileEdit] = useState(false);
  // Shared state sync
  const [syncedBills, setSyncedBills] = useState<BillRecord[]>([]);
  const [mySafetyTasks, setMySafetyTasks] = useState<SafetyTask[]>([]);
  const [syncTime, setSyncTime] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Load shared state data
  const loadSharedData = () => {
    const bills = getBills();
    setSyncedBills(bills);
    const allTasks = getSafetyTasks();
    setMySafetyTasks(allTasks);
    setSyncTime(new Date().toLocaleTimeString("zh-CN", { hour12: false }));
  };

  useEffect(() => {
    loadSharedData();
    const interval = setInterval(loadSharedData, 8000);
    return () => clearInterval(interval);
  }, []);

  // Draw usage chart
  useEffect(() => {
    if (tab !== "home") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const data = [18.5, 22.3, 28.6, 31.2, 25.8, 24.1];
    const labels = ["11月", "12月", "1月", "2月", "3月", "4月"];
    const W = canvas.width;
    const H = canvas.height;
    const maxVal = Math.max(...data);
    const barW = (W - 40) / data.length;
    ctx.clearRect(0, 0, W, H);
    data.forEach((v, i) => {
      const barH = ((v / maxVal) * (H - 30));
      const x = 20 + i * barW + barW * 0.15;
      const y = H - 20 - barH;
      const grad = ctx.createLinearGradient(0, y, 0, H - 20);
      grad.addColorStop(0, i === 5 ? "#E8531A" : "#f97316");
      grad.addColorStop(1, i === 5 ? "rgba(232,83,26,0.3)" : "rgba(249,115,22,0.2)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(x, y, barW * 0.7, barH, 3);
      ctx.fill();
      ctx.fillStyle = "#9ca3af";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(labels[i], x + barW * 0.35, H - 5);
    });
  }, [tab]);

  const handlePurchase = () => {
    // Sync payment to shared state
    const unpaidBill = syncedBills.find(b => b.status === "待缴费");
    if (unpaidBill) {
      payBill(unpaidBill.id);
      loadSharedData();
    }
    setPaySuccess(true);
    setTimeout(() => setPaySuccess(false), 3000);
  };

  const handleRepair = () => {
    if (!repairType || !repairDesc) return;
    setRepairSubmitted(true);
  };

  const handleSafety = () => {
    if (!safetyDate || !safetyTimeSlot) return;
    setSafetySubmitted(true);
  };

  const handleInvoiceApply = () => {
    if (!invoiceEmail) return;
    setInvoiceApplied(true);
    setTimeout(() => setInvoiceApplied(false), 3000);
  };

  const unreadCount = newsItems.filter(n => !n.read).length;

  // Use synced bills if available, fallback to mock
  const displayBills = syncedBills.length > 0
    ? syncedBills.map(b => ({
        id: b.id,
        month: b.month,
        amount: b.amount,
        gasUsed: b.gasUsed,
        status: b.status === "已缴费" ? "已缴费" : b.status === "待缴费" ? "已出账" : "逾期",
        dueDate: b.dueDate,
        paid: b.status === "已缴费",
        unitPrice: 3.45,
      }))
    : billRecords;

  // My safety appointment from shared state
  const myAppointment = mySafetyTasks.find(t => t.status !== "已完成" && t.status !== "已取消");

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-8">
      {/* Mobile frame */}
      <div className="w-[390px] bg-white relative overflow-hidden flex flex-col" style={{ height: "844px", boxShadow: "0 20px 80px rgba(0,0,0,0.2)", borderRadius: "40px" }}>
        {/* Status bar */}
        <div className="bg-[#E8531A] px-6 pt-4 pb-0 flex items-center justify-between flex-shrink-0">
          <span className="text-white text-xs font-semibold">9:41</span>
          <div className="flex items-center gap-1.5">
            <i className="ri-signal-wifi-3-line text-white text-xs"></i>
            <i className="ri-signal-tower-line text-white text-xs"></i>
            <i className="ri-battery-2-charge-line text-white text-xs"></i>
          </div>
        </div>

        {/* Header */}
        <div className="bg-[#E8531A] px-5 pb-5 pt-2 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-white/70 text-xs">AI智慧燃气</div>
              <div className="text-white font-bold text-base">用户自助服务</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="relative w-9 h-9 rounded-full bg-white/20 flex items-center justify-center cursor-pointer"
                onClick={() => setTab("news")}
              >
                <i className="ri-notification-3-line text-white text-lg"></i>
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-white text-[9px] flex items-center justify-center font-bold">{unreadCount}</span>
                )}
              </button>
              <button className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center cursor-pointer" onClick={() => setTab("profile")}>
                <i className="ri-user-line text-white text-lg"></i>
              </button>
            </div>
          </div>

          {/* Balance card */}
          <div className="bg-white/15 rounded-2xl p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80 text-xs">账户余额</span>
              <div className="flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-green-300 animate-pulse"></div>
                <span className="text-white/50 text-[10px]">已同步 {syncTime}</span>
              </div>
            </div>
            <div className="text-white text-3xl font-bold mb-2">¥{mockUser.balance.toFixed(2)}</div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-white/70 text-xs">
                <span>本月 {mockUser.gasUsed} m³</span>
                <span>·</span>
                <span>上次购气 {mockUser.lastPurchase}</span>
              </div>
              <button
                onClick={() => setTab("purchase")}
                className="bg-white text-[#E8531A] text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap"
              >
                立即缴费
              </button>
            </div>
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto">

          {/* ===== HOME TAB ===== */}
          {tab === "home" && (
            <div className="p-4 space-y-4">
              {/* Quick actions */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: "账单查询", icon: "ri-file-list-3-line", color: "bg-orange-50 text-[#E8531A]", action: () => { setTab("balance"); setBalanceSubTab("bills"); } },
                  { label: "在线缴费", icon: "ri-wallet-3-line", color: "bg-green-50 text-green-600", action: () => setTab("purchase") },
                  { label: "报修服务", icon: "ri-tools-line", color: "bg-amber-50 text-amber-600", action: () => setTab("repair") },
                  { label: "安检预约", icon: "ri-shield-check-line", color: "bg-teal-50 text-teal-600", action: () => setTab("safety") },
                  { label: "电子发票", icon: "ri-receipt-line", color: "bg-purple-50 text-purple-600", action: () => { setTab("balance"); setBalanceSubTab("invoice"); } },
                  { label: "报装进度", icon: "ri-building-line", color: "bg-sky-50 text-sky-600", action: () => setTab("install") },
                  { label: "用能分析", icon: "ri-bar-chart-2-line", color: "bg-rose-50 text-rose-600", action: () => setTab("energy") },
                  { label: "购气分析", icon: "ri-line-chart-line", color: "bg-indigo-50 text-indigo-600", action: () => setTab("purchaseAnalysis") },
                  { label: "AI对比", icon: "ri-robot-2-line", color: "bg-teal-50 text-teal-600", action: () => setTab("aiReport") },
                  { label: "消息通知", icon: "ri-notification-3-line", color: "bg-amber-50 text-amber-600", action: () => setTab("news") },
                ].map((item, i) => (
                  <button key={i} onClick={item.action} className="flex flex-col items-center gap-1.5 cursor-pointer">
                    <div className={`w-14 h-14 rounded-2xl ${item.color} flex items-center justify-center`}>
                      <i className={`${item.icon} text-2xl`}></i>
                    </div>
                    <span className="text-[10px] text-gray-600 text-center leading-tight">{item.label}</span>
                  </button>
                ))}
              </div>

              {/* Alert banner */}
              {newsItems.filter(n => !n.read).length > 0 && (
                <button onClick={() => setTab("news")} className="w-full bg-amber-50 rounded-xl p-3 flex items-start gap-2 cursor-pointer text-left">
                  <i className="ri-notification-3-line text-amber-500 text-sm mt-0.5 flex-shrink-0"></i>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-amber-700">您有 {unreadCount} 条未读消息</div>
                    <div className="text-xs text-amber-600 mt-0.5 truncate">{newsItems.find(n => !n.read)?.title}</div>
                  </div>
                  <i className="ri-arrow-right-s-line text-amber-400 flex-shrink-0"></i>
                </button>
              )}

              {/* Safety appointment sync notice */}
              {myAppointment && (
                <div className="bg-teal-50 rounded-xl p-3 flex items-start gap-2">
                  <i className="ri-shield-check-line text-teal-500 text-sm mt-0.5 flex-shrink-0"></i>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-teal-700">安检任务已安排</div>
                    <div className="text-xs text-teal-600 mt-0.5">{myAppointment.scheduledDate} {myAppointment.scheduledTime} · {myAppointment.inspectorName}上门</div>
                  </div>
                  <span className="text-[10px] bg-teal-100 text-teal-600 px-1.5 py-0.5 rounded font-medium flex-shrink-0">{myAppointment.status}</span>
                </div>
              )}

              {/* Current bill */}
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-800">本月账单</span>
                  <button onClick={() => { setTab("balance"); setBalanceSubTab("bills"); }} className="text-xs text-[#E8531A] cursor-pointer">查看全部</button>
                </div>
                {displayBills.length > 0 ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">¥{displayBills[0].amount.toFixed(2)}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{displayBills[0].month} · {displayBills[0].gasUsed} m³</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-xs px-2 py-1 rounded-full font-medium ${displayBills[0].paid ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>
                        {displayBills[0].status}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-400">暂无账单数据</div>
                )}
              </div>

              {/* Usage chart */}
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-800">近6月用气量</span>
                  <span className="text-xs text-gray-400">单位: m³</span>
                </div>
                <canvas ref={canvasRef} width={340} height={100} className="w-full" />
              </div>

              {/* Safety score */}
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-800">安全评分</span>
                  <button onClick={() => setTab("safety")} className="text-xs text-[#E8531A] cursor-pointer">查看详情</button>
                </div>
                <div className="flex items-center gap-4">
                  <div className="relative w-16 h-16 flex-shrink-0">
                    <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
                      <circle cx="32" cy="32" r="26" fill="none" stroke="#f3f4f6" strokeWidth="6" />
                      <circle cx="32" cy="32" r="26" fill="none" stroke="#E8531A" strokeWidth="6"
                        strokeDasharray={`${(mockUser.safetyScore / 100) * 163.4} 163.4`}
                        strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-sm font-bold text-gray-800">{mockUser.safetyScore}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-800 mb-1">安全状态良好</div>
                    <div className="grid grid-cols-3 gap-1">
                      {safetyCheckItems.map((item, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <div className={`w-1.5 h-1.5 rounded-full ${item.status === "正常" || item.status === "良好" ? "bg-green-500" : "bg-amber-500"}`}></div>
                          <span className="text-[10px] text-gray-500">{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Service hotline */}
              <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-800">24小时服务热线</div>
                  <div className="text-xs text-gray-500 mt-0.5">燃气泄漏请立即拨打</div>
                </div>
                <a href="tel:400-000-0000" className="bg-[#E8531A] text-white px-4 py-2 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap">400-000-0000</a>
              </div>
            </div>
          )}

          {/* ===== BALANCE / BILLS / HISTORY / INVOICE TAB ===== */}
          {tab === "balance" && (
            <div className="p-4 space-y-3">
              <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                {[
                  { key: "balance", label: "余额" },
                  { key: "bills", label: "账单" },
                  { key: "history", label: "缴费" },
                  { key: "invoice", label: "发票" },
                ].map(st => (
                  <button
                    key={st.key}
                    onClick={() => setBalanceSubTab(st.key as typeof balanceSubTab)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${balanceSubTab === st.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}
                  >
                    {st.label}
                  </button>
                ))}
              </div>

              {/* Balance detail */}
              {balanceSubTab === "balance" && (
                <>
                  <div className="bg-gradient-to-br from-[#E8531A] to-[#f97316] rounded-2xl p-5 text-white">
                    <div className="text-white/80 text-xs mb-1">当前余额</div>
                    <div className="text-4xl font-bold mb-3">¥{mockUser.balance.toFixed(2)}</div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-white/15 rounded-xl p-2.5">
                        <div className="text-white/70 text-[10px]">本月用气</div>
                        <div className="text-white font-bold text-sm">{mockUser.gasUsed} m³</div>
                      </div>
                      <div className="bg-white/15 rounded-xl p-2.5">
                        <div className="text-white/70 text-[10px]">本月费用</div>
                        <div className="text-white font-bold text-sm">¥{(mockUser.gasUsed * 3.45).toFixed(2)}</div>
                      </div>
                      <div className="bg-white/15 rounded-xl p-2.5">
                        <div className="text-white/70 text-[10px]">气价</div>
                        <div className="text-white font-bold text-sm">3.45元/m³</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-100 p-4">
                    <div className="text-sm font-semibold text-gray-800 mb-3">近期用气记录</div>
                    {[
                      { date: "2026-04-10", amount: 2.3, cost: 7.94 },
                      { date: "2026-04-09", amount: 3.1, cost: 10.70 },
                      { date: "2026-04-08", amount: 2.8, cost: 9.66 },
                      { date: "2026-04-07", amount: 2.5, cost: 8.63 },
                      { date: "2026-04-06", amount: 3.4, cost: 11.73 },
                      { date: "2026-04-05", amount: 2.9, cost: 10.01 },
                    ].map((r, i) => (
                      <div key={i} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                        <div>
                          <div className="text-sm text-gray-800">{r.date}</div>
                          <div className="text-xs text-gray-400">用气量 {r.amount} m³</div>
                        </div>
                        <div className="text-sm font-medium text-gray-800">-¥{r.cost}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Bills - using synced data */}
              {balanceSubTab === "bills" && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1 h-1 rounded-full bg-green-500 animate-pulse"></div>
                      <span>共 {displayBills.length} 条账单 · 实时同步</span>
                    </div>
                    <span className="text-amber-600">待缴 ¥{displayBills.filter(b => !b.paid).reduce((s, b) => s + b.amount, 0).toFixed(2)}</span>
                  </div>
                  {displayBills.map((bill, i) => (
                    <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 cursor-pointer" onClick={() => setBillDetail(bill)}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-sm font-semibold text-gray-800">{bill.month}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{bill.gasUsed} m³ · {bill.unitPrice}元/m³</div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${bill.paid ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>
                          {bill.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-xl font-bold text-gray-900">¥{bill.amount.toFixed(2)}</div>
                        <div className="flex gap-2">
                          {!bill.paid ? (
                            <button onClick={e => { e.stopPropagation(); setTab("purchase"); }} className="bg-[#E8531A] text-white px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap">立即缴费</button>
                          ) : (
                            <button onClick={e => { e.stopPropagation(); setInvoiceModal(bill); }} className="border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg text-xs cursor-pointer whitespace-nowrap">
                              <i className="ri-receipt-line mr-1"></i>发票
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Payment History */}
              {balanceSubTab === "history" && (
                <div className="space-y-2">
                  <div className="bg-orange-50 rounded-xl p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ri-history-line text-[#E8531A] text-sm"></i>
                      <span className="text-xs text-[#E8531A]">共 {payHistoryRecords.length} 条缴费记录</span>
                    </div>
                    <span className="text-xs text-gray-500">累计缴费 ¥{payHistoryRecords.reduce((s, r) => s + r.amount, 0).toFixed(2)}</span>
                  </div>
                  {payHistoryRecords.map((rec, i) => (
                    <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0">
                          <i className={`${rec.icon} ${rec.iconColor} text-xl`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold text-gray-800">{rec.type}</div>
                            <div className="text-base font-bold text-[#E8531A]">¥{rec.amount.toFixed(2)}</div>
                          </div>
                          <div className="flex items-center justify-between mt-0.5">
                            <span className="text-xs text-gray-400">{rec.date} · {rec.method}</span>
                            <span className="text-xs text-green-600 font-medium">{rec.status}</span>
                          </div>
                          <div className="text-[10px] text-gray-300 mt-0.5">流水号: {rec.id}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Invoice */}
              {balanceSubTab === "invoice" && (
                <div className="space-y-3">
                  {invoiceApplied ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-check-double-line text-green-500 text-3xl"></i>
                      </div>
                      <div className="text-base font-bold text-gray-900 mb-1">开票申请已提交</div>
                      <div className="text-xs text-gray-500">发票将在1-3个工作日内发送至您的邮箱</div>
                      <button onClick={() => setInvoiceApplied(false)} className="mt-4 text-xs text-[#E8531A] cursor-pointer">继续申请</button>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl border border-gray-100 p-4">
                      <div className="text-sm font-semibold text-gray-800 mb-3">申请电子发票</div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-gray-400 mb-1.5 block">选择账单</label>
                          <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E8531A]">
                            {displayBills.filter(b => b.paid).map((b, i) => (
                              <option key={i}>{b.month} · ¥{b.amount.toFixed(2)}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 mb-1.5 block">发票类型</label>
                          <select value={invoiceType} onChange={e => setInvoiceType(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E8531A]">
                            <option>增值税普通发票</option>
                            <option>增值税专用发票</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 mb-1.5 block">抬头类型</label>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="radio" name="titleType" checked={titleType === "personal"} onChange={() => setTitleType("personal")} className="accent-[#E8531A]" />
                              <span className="text-xs text-gray-700">个人</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="radio" name="titleType" checked={titleType === "company"} onChange={() => setTitleType("company")} className="accent-[#E8531A]" />
                              <span className="text-xs text-gray-700">企业</span>
                            </label>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 mb-1.5 block">发票抬头</label>
                          <input type="text" defaultValue={titleType === "personal" ? mockUser.name : ""} placeholder={titleType === "company" ? "请输入企业名称" : mockUser.name} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E8531A]" />
                        </div>
                        {titleType === "company" && (
                          <div>
                            <label className="text-xs text-gray-400 mb-1.5 block">税号</label>
                            <input type="text" placeholder="请输入纳税人识别号" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E8531A]" />
                          </div>
                        )}
                        <div>
                          <label className="text-xs text-gray-400 mb-1.5 block">接收邮箱 <span className="text-red-400">*</span></label>
                          <input type="email" name="email" placeholder="请输入邮箱地址" value={invoiceEmail} onChange={e => setInvoiceEmail(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E8531A]" />
                        </div>
                        <button onClick={handleInvoiceApply} disabled={!invoiceEmail} className="w-full bg-[#E8531A] text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer disabled:opacity-50 whitespace-nowrap">申请开票</button>
                      </div>
                    </div>
                  )}

                  <div className="bg-white rounded-xl border border-gray-100 p-4">
                    <div className="text-sm font-semibold text-gray-800 mb-3">历史发票</div>
                    {displayBills.filter(b => b.paid).map((bill, i) => (
                      <div key={i} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                        <div>
                          <div className="text-xs font-medium text-gray-800">{bill.month}</div>
                          <div className="text-[10px] text-gray-400">¥{bill.amount.toFixed(2)} · 增值税普通发票</div>
                        </div>
                        <button onClick={() => setInvoiceModal(bill)} className="flex items-center gap-1 text-xs text-[#E8531A] cursor-pointer whitespace-nowrap">
                          <i className="ri-download-line"></i>下载
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== PURCHASE TAB ===== */}
          {tab === "purchase" && (
            <div className="p-4 space-y-4">
              <div className="text-sm font-semibold text-gray-800">在线购气缴费</div>
              {paySuccess ? (
                <div className="text-center py-10">
                  <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-double-line text-green-500 text-4xl"></i>
                  </div>
                  <div className="text-lg font-bold text-gray-900 mb-1">缴费成功！</div>
                  <div className="text-sm text-gray-500">¥{customAmount || selectedAmount} 已充入您的账户</div>
                  <div className="text-xs text-gray-400 mt-1">新余额: ¥{(mockUser.balance + Number(customAmount || selectedAmount)).toFixed(2)}</div>
                  <div className="text-xs text-green-500 mt-1 flex items-center justify-center gap-1">
                    <i className="ri-check-line"></i>账单状态已同步更新
                  </div>
                  <div className="flex gap-3 justify-center mt-5">
                    <button onClick={() => { setTab("balance"); setBalanceSubTab("history"); }} className="text-xs text-[#E8531A] border border-[#E8531A] px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap">查看记录</button>
                    <button onClick={() => { setTab("balance"); setBalanceSubTab("invoice"); }} className="text-xs text-gray-600 border border-gray-200 px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap">申请发票</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-orange-50 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-500 mb-0.5">当前余额</div>
                      <div className="text-2xl font-bold text-[#E8531A]">¥{mockUser.balance.toFixed(2)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500 mb-0.5">本月待缴</div>
                      <div className="text-lg font-bold text-amber-600">¥{displayBills.filter(b => !b.paid).reduce((s, b) => s + b.amount, 0).toFixed(2) || "0.00"}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-2">选择缴费金额</div>
                    <div className="grid grid-cols-4 gap-2">
                      {purchaseAmounts.map(amt => (
                        <button key={amt} onClick={() => { setSelectedAmount(amt); setCustomAmount(""); }} className={`py-3 rounded-xl text-sm font-bold border-2 transition-colors cursor-pointer whitespace-nowrap ${selectedAmount === amt && !customAmount ? "border-[#E8531A] bg-orange-50 text-[#E8531A]" : "border-gray-100 text-gray-700"}`}>¥{amt}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-2">自定义金额</div>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">¥</span>
                      <input type="number" placeholder="输入金额（最低10元）" value={customAmount} onChange={e => { setCustomAmount(e.target.value); setSelectedAmount(0); }} className="w-full pl-7 pr-3 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8531A]" />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-2">支付方式</div>
                    <div className="space-y-2">
                      {[
                        { key: "wechat", icon: "ri-wechat-pay-line", label: "微信支付", color: "text-green-600" },
                        { key: "alipay", icon: "ri-alipay-line", label: "支付宝", color: "text-sky-500" },
                        { key: "card", icon: "ri-bank-card-line", label: "银行卡", color: "text-gray-600" },
                        { key: "auto", icon: "ri-bank-line", label: "银行代扣", color: "text-gray-600", sub: "已签约工商银行尾号6789" },
                      ].map(p => (
                        <label key={p.key} className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-colors ${payMethod === p.key ? "border-[#E8531A] bg-orange-50/50" : "border-gray-100"}`}>
                          <input type="radio" name="payMethod" checked={payMethod === p.key} onChange={() => setPayMethod(p.key)} className="accent-[#E8531A]" />
                          <i className={`${p.icon} ${p.color} text-xl`}></i>
                          <div>
                            <div className="text-sm text-gray-700">{p.label}</div>
                            {p.sub && <div className="text-[10px] text-gray-400">{p.sub}</div>}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <button onClick={handlePurchase} className="w-full bg-[#E8531A] text-white py-4 rounded-2xl text-base font-bold cursor-pointer hover:bg-[#d44a15] transition-colors whitespace-nowrap">
                    确认缴费 ¥{customAmount || selectedAmount}
                  </button>
                </>
              )}
            </div>
          )}

          {/* ===== REPAIR TAB ===== */}
          {tab === "repair" && (
            <div className="p-4 space-y-4">
              <div className="text-sm font-semibold text-gray-800">在线报修</div>
              {repairSubmitted ? (
                <div className="text-center py-10">
                  <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-double-line text-green-500 text-4xl"></i>
                  </div>
                  <div className="text-lg font-bold text-gray-900 mb-1">报修成功！</div>
                  <div className="text-sm text-gray-500 mb-1">工单编号: WO-2026-5821</div>
                  <div className="text-xs text-gray-400">预计2小时内安排师傅上门</div>
                  <div className="mt-4 bg-gray-50 rounded-xl p-4 text-left">
                    <div className="text-xs font-medium text-gray-700 mb-2">工单进度</div>
                    {["已提交", "已受理", "派单中", "上门处理", "完成"].map((s, i) => (
                      <div key={i} className="flex items-center gap-2 mb-1.5">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${i <= 1 ? "bg-[#E8531A]" : "bg-gray-200"}`}>
                          {i <= 1 && <i className="ri-check-line text-white text-[8px]"></i>}
                        </div>
                        <span className={`text-xs ${i <= 1 ? "text-gray-800 font-medium" : "text-gray-400"}`}>{s}</span>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setRepairSubmitted(false)} className="mt-4 text-[#E8531A] text-sm cursor-pointer">继续报修</button>
                </div>
              ) : (
                <>
                  <div>
                    <div className="text-xs text-gray-500 mb-2">故障类型</div>
                    <div className="grid grid-cols-3 gap-2">
                      {["燃气泄漏", "设备故障", "管道问题", "阀门损坏", "表计异常", "其他"].map(type => (
                        <button key={type} onClick={() => setRepairType(type)} className={`py-2.5 rounded-xl text-xs font-medium border-2 transition-colors cursor-pointer whitespace-nowrap ${repairType === type ? "border-[#E8531A] bg-orange-50 text-[#E8531A]" : "border-gray-100 text-gray-600"}`}>{type}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-2">故障描述</div>
                    <textarea rows={4} maxLength={500} placeholder="请详细描述故障情况..." value={repairDesc} onChange={e => setRepairDesc(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#E8531A] resize-none" />
                    <div className="text-right text-[10px] text-gray-300 mt-0.5">{repairDesc.length}/500</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-2">联系方式</div>
                    <input type="tel" defaultValue="138****5678" className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#E8531A]" />
                  </div>
                  <div className="bg-red-50 rounded-xl p-3 flex items-start gap-2">
                    <i className="ri-error-warning-line text-red-500 text-sm mt-0.5 flex-shrink-0"></i>
                    <div className="text-xs text-red-600">如发现燃气泄漏，请立即关闭阀门、开窗通风，拨打 <strong>400-000-0000</strong></div>
                  </div>
                  <button onClick={handleRepair} disabled={!repairType || !repairDesc} className="w-full bg-[#E8531A] text-white py-4 rounded-2xl text-base font-bold cursor-pointer hover:bg-[#d44a15] transition-colors disabled:opacity-50 whitespace-nowrap">提交报修</button>
                </>
              )}
            </div>
          )}

          {/* ===== SAFETY TAB ===== */}
          {tab === "safety" && (
            <div className="p-4 space-y-4">
              <div className="text-sm font-semibold text-gray-800">安检服务</div>

              {/* Safety score card */}
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-4 text-white">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-white/80 text-xs">安全评分</div>
                    <div className="text-3xl font-bold">{mockUser.safetyScore}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-white/80 text-xs">上次安检</div>
                    <div className="text-sm font-medium">2025-12-15</div>
                    <div className="text-white/80 text-xs mt-1">下次建议</div>
                    <div className="text-sm font-medium">{mockUser.nextSafety}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {safetyCheckItems.map((item, i) => (
                    <div key={i} className="bg-white/15 rounded-lg p-2 flex items-center gap-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.status === "正常" || item.status === "良好" ? "bg-green-300" : "bg-amber-300"}`}></div>
                      <div>
                        <div className="text-white text-[10px] font-medium">{item.label}</div>
                        <div className={`text-[9px] ${item.status === "正常" || item.status === "良好" ? "text-green-200" : "text-amber-200"}`}>{item.status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Show existing appointment from backend */}
              {myAppointment && !safetySubmitted && (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-calendar-check-line text-teal-600 text-sm"></i>
                    <span className="text-sm font-semibold text-teal-700">已有安检安排</span>
                    <span className="ml-auto text-[10px] bg-teal-100 text-teal-600 px-2 py-0.5 rounded-full font-medium">{myAppointment.status}</span>
                  </div>
                  <div className="space-y-1 text-xs text-teal-600">
                    <div><i className="ri-calendar-line mr-1"></i>{myAppointment.scheduledDate} {myAppointment.scheduledTime}</div>
                    <div><i className="ri-user-line mr-1"></i>安检员: {myAppointment.inspectorName}</div>
                    <div><i className="ri-map-pin-line mr-1"></i>{myAppointment.address}</div>
                    {myAppointment.riskLevel && (
                      <div><i className="ri-shield-check-line mr-1"></i>风险等级: {myAppointment.riskLevel}</div>
                    )}
                  </div>
                </div>
              )}

              {safetySubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-calendar-check-line text-green-500 text-3xl"></i>
                  </div>
                  <div className="text-base font-bold text-gray-900 mb-1">预约成功！</div>
                  <div className="text-sm text-gray-500 mb-0.5">预约日期: {safetyDate}</div>
                  <div className="text-xs text-gray-400">时间段: {safetyTimeSlot}</div>
                  <button onClick={() => { setSafetySubmitted(false); setSafetyDate(""); setSafetyTimeSlot(""); }} className="mt-4 text-[#E8531A] text-sm cursor-pointer">重新预约</button>
                </div>
              ) : (
                <>
                  <div>
                    <div className="text-xs text-gray-500 mb-2">选择预约日期</div>
                    <input type="date" min="2026-04-11" value={safetyDate} onChange={e => setSafetyDate(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#E8531A]" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-2">选择时间段</div>
                    <div className="grid grid-cols-2 gap-2">
                      {["09:00-11:00", "11:00-13:00", "14:00-16:00", "16:00-18:00", "18:00-20:00", "随时上门"].map(t => (
                        <button key={t} onClick={() => setSafetyTimeSlot(t)} className={`py-2.5 rounded-xl text-xs border-2 transition-colors cursor-pointer whitespace-nowrap ${safetyTimeSlot === t ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-100 text-gray-600"}`}>{t}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-2">安检地址</div>
                    <div className="border border-gray-200 rounded-xl px-3 py-3 text-sm text-gray-700 bg-gray-50">{mockUser.address}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-2">备注（选填）</div>
                    <textarea rows={2} maxLength={200} placeholder="如有特殊情况请说明..." className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-teal-500 resize-none" />
                  </div>
                  <button onClick={handleSafety} disabled={!safetyDate || !safetyTimeSlot} className="w-full bg-teal-500 text-white py-4 rounded-2xl text-base font-bold cursor-pointer hover:bg-teal-600 transition-colors disabled:opacity-50 whitespace-nowrap">确认预约</button>
                </>
              )}
            </div>
          )}

          {/* ===== INSTALL TAB ===== */}
          {tab === "install" && (
            <div className="p-4 space-y-4">
              <div className="text-sm font-semibold text-gray-800">报装进度</div>

              <div className="bg-gradient-to-br from-sky-500 to-sky-600 rounded-2xl p-4 text-white">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-white/80 text-xs">报装编号</div>
                    <div className="text-base font-bold">BZ-2026-001234</div>
                  </div>
                  <div className="bg-green-400 text-white text-xs px-3 py-1 rounded-full font-medium">已完成</div>
                </div>
                <div className="text-white/70 text-xs">{mockUser.address}</div>
                <div className="mt-3 bg-white/15 rounded-xl p-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-white/70">申请日期</span>
                    <span className="text-white">2026-01-05</span>
                  </div>
                  <div className="flex items-center justify-between text-xs mt-1">
                    <span className="text-white/70">完成日期</span>
                    <span className="text-white">2026-01-30</span>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="text-sm font-semibold text-gray-800 mb-4">办理进度</div>
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-100"></div>
                  {installSteps.map((step, i) => (
                    <div key={i} className="relative flex gap-4 mb-4 last:mb-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${step.status === "done" ? "bg-[#E8531A]" : "bg-gray-200"}`}>
                        {step.status === "done" ? (
                          <i className="ri-check-line text-white text-sm"></i>
                        ) : (
                          <span className="text-gray-400 text-xs font-bold">{step.step}</span>
                        )}
                      </div>
                      <div className="flex-1 pb-1">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-800">{step.label}</div>
                          <div className="text-xs text-gray-400">{step.date}</div>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{step.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button className="w-full border border-[#E8531A] text-[#E8531A] py-3 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap">
                <i className="ri-add-line mr-1"></i>新增报装申请
              </button>
            </div>
          )}

          {/* ===== NEWS TAB ===== */}
          {tab === "news" && (
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-800">消息通知</div>
                <button className="text-xs text-[#E8531A] cursor-pointer whitespace-nowrap">全部已读</button>
              </div>
              {newsItems.map((item, i) => (
                <button key={i} onClick={() => setSelectedNews(item)} className="w-full bg-white rounded-xl border border-gray-100 p-4 text-left cursor-pointer hover:border-[#E8531A]/20 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      item.type === "通知" ? "bg-orange-50" :
                      item.type === "安全" ? "bg-red-50" :
                      item.type === "活动" ? "bg-green-50" : "bg-amber-50"
                    }`}>
                      <i className={`text-sm ${
                        item.type === "通知" ? "ri-notification-3-line text-[#E8531A]" :
                        item.type === "安全" ? "ri-shield-check-line text-red-500" :
                        item.type === "活动" ? "ri-gift-line text-green-500" : "ri-tools-line text-amber-500"
                      }`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          item.type === "通知" ? "bg-orange-50 text-[#E8531A]" :
                          item.type === "安全" ? "bg-red-50 text-red-600" :
                          item.type === "活动" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"
                        }`}>{item.type}</span>
                        {!item.read && <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>}
                      </div>
                      <div className="text-sm font-medium text-gray-800 leading-tight">{item.title}</div>
                      <div className="text-xs text-gray-400 mt-1">{item.date}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* ===== ENERGY ANALYSIS TAB ===== */}
          {tab === "energy" && <EnergyAnalysis />}

          {/* ===== PURCHASE ANALYSIS TAB ===== */}
          {tab === "purchaseAnalysis" && <PurchaseAnalysis />}

          {/* ===== AI COMPARE REPORT TAB ===== */}
          {tab === "aiReport" && <AICompareReport />}

          {/* ===== PROFILE TAB ===== */}
          {tab === "profile" && (
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-800">个人中心</div>
                <button onClick={() => setProfileEdit(!profileEdit)} className="text-xs text-[#E8531A] cursor-pointer whitespace-nowrap">
                  {profileEdit ? "保存" : "编辑"}
                </button>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-16 h-16 rounded-full bg-[#E8531A] flex items-center justify-center">
                    <span className="text-white text-2xl font-bold">{mockUser.name[0]}</span>
                  </div>
                  <div>
                    <div className="text-base font-bold text-gray-900">{mockUser.name}</div>
                    <div className="text-xs text-gray-500">用户编号: {mockUser.userId}</div>
                    <div className="flex items-center gap-1 mt-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                      <span className="text-xs text-green-600">正常用气</span>
                    </div>
                  </div>
                </div>
                {[
                  { label: "安装地址", value: mockUser.address, icon: "ri-map-pin-line" },
                  { label: "表计编号", value: mockUser.meterNo, icon: "ri-dashboard-line" },
                  { label: "联系电话", value: mockUser.phone, icon: "ri-phone-line" },
                  { label: "绑定邮箱", value: mockUser.email, icon: "ri-mail-line" },
                  { label: "下次安检", value: mockUser.nextSafety, icon: "ri-shield-check-line" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
                    <i className={`${item.icon} text-gray-400 text-sm w-4 flex-shrink-0`}></i>
                    <span className="text-xs text-gray-400 w-16 flex-shrink-0">{item.label}</span>
                    {profileEdit && item.label !== "表计编号" && item.label !== "下次安检" ? (
                      <input defaultValue={item.value} className="flex-1 text-xs text-gray-700 border-b border-[#E8531A] focus:outline-none bg-transparent" />
                    ) : (
                      <span className="text-xs text-gray-700 flex-1">{item.value}</span>
                    )}
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                {[
                  { icon: "ri-file-list-3-line", label: "账单查询", action: () => { setTab("balance"); setBalanceSubTab("bills"); } },
                  { icon: "ri-history-line", label: "缴费记录", action: () => { setTab("balance"); setBalanceSubTab("history"); } },
                  { icon: "ri-receipt-line", label: "电子发票", action: () => { setTab("balance"); setBalanceSubTab("invoice"); } },
                  { icon: "ri-building-line", label: "报装进度", action: () => setTab("install") },
                  { icon: "ri-shield-check-line", label: "安检预约", action: () => setTab("safety") },
                  { icon: "ri-customer-service-2-line", label: "联系客服", action: () => {} },
                  { icon: "ri-question-line", label: "常见问题", action: () => {} },
                  { icon: "ri-settings-3-line", label: "账户设置", action: () => {} },
                ].map((item, i) => (
                  <button key={i} onClick={item.action} className="w-full flex items-center justify-between p-3.5 bg-white rounded-xl border border-gray-100 cursor-pointer hover:border-[#E8531A]/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <i className={`${item.icon} text-[#E8531A] text-lg`}></i>
                      <span className="text-sm text-gray-700">{item.label}</span>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-400"></i>
                  </button>
                ))}
              </div>

              <button className="w-full border border-gray-200 text-gray-500 py-3 rounded-xl text-sm cursor-pointer whitespace-nowrap">
                退出登录
              </button>
            </div>
          )}
        </div>

        {/* Bottom nav */}
        <div className="border-t border-gray-100 bg-white px-1 py-2 flex items-center justify-around flex-shrink-0">
          {[
            { key: "home", icon: "ri-home-4-line", label: "首页" },
            { key: "balance", icon: "ri-wallet-3-line", label: "账单" },
            { key: "energy", icon: "ri-bar-chart-2-line", label: "用能" },
            { key: "purchaseAnalysis", icon: "ri-line-chart-line", label: "购气" },
            { key: "aiReport", icon: "ri-robot-2-line", label: "AI报告" },
            { key: "profile", icon: "ri-user-line", label: "我的" },
          ].map(item => (
            <button key={item.key} onClick={() => setTab(item.key as TabType)} className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-xl transition-colors cursor-pointer ${tab === item.key ? "text-[#E8531A]" : "text-gray-400"}`}>
              <i className={`${item.icon} text-lg`}></i>
              <span className="text-[9px]">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Invoice Download Modal */}
      {invoiceModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setInvoiceModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-[360px]" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-5">
              <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-3">
                <i className="ri-receipt-line text-[#E8531A] text-3xl"></i>
              </div>
              <h3 className="text-base font-bold text-gray-900">电子发票</h3>
              <p className="text-xs text-gray-400 mt-1">{invoiceModal.month} · ¥{invoiceModal.amount.toFixed(2)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 mb-4">
              {[
                { label: "发票号码", value: `FP-2026-${invoiceModal.id.slice(-4)}` },
                { label: "开票日期", value: invoiceModal.dueDate },
                { label: "发票金额", value: `¥${invoiceModal.amount.toFixed(2)}` },
                { label: "用气量", value: `${invoiceModal.gasUsed} m³` },
                { label: "购买方", value: mockUser.name },
                { label: "销售方", value: "XX燃气有限公司" },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">{item.label}</span>
                  <span className="text-gray-700 font-medium">{item.value}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setInvoiceModal(null)} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap">关闭</button>
              <button className="flex-1 bg-[#E8531A] text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap">
                <i className="ri-download-line mr-1"></i>下载PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bill Detail Modal */}
      {billDetail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setBillDetail(null)}>
          <div className="bg-white rounded-2xl p-6 w-[360px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900">账单详情</h3>
              <button onClick={() => setBillDetail(null)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="text-center mb-4">
              <div className="text-3xl font-bold text-gray-900">¥{billDetail.amount.toFixed(2)}</div>
              <div className="text-sm text-gray-500 mt-1">{billDetail.month}</div>
              <span className={`text-xs px-2 py-1 rounded-full font-medium mt-2 inline-block ${billDetail.paid ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>{billDetail.status}</span>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 mb-4">
              {[
                { label: "账单编号", value: billDetail.id },
                { label: "用气量", value: `${billDetail.gasUsed} m³` },
                { label: "单价", value: `${billDetail.unitPrice}元/m³` },
                { label: "截止日期", value: billDetail.dueDate },
                { label: "用气地址", value: mockUser.address },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">{item.label}</span>
                  <span className="text-gray-700 font-medium text-right max-w-48">{item.value}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              {!billDetail.paid ? (
                <button onClick={() => { setBillDetail(null); setTab("purchase"); }} className="flex-1 bg-[#E8531A] text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap">立即缴费</button>
              ) : (
                <button onClick={() => { setBillDetail(null); setInvoiceModal(billDetail); }} className="flex-1 bg-[#E8531A] text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap">下载发票</button>
              )}
              <button onClick={() => setBillDetail(null)} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap">关闭</button>
            </div>
          </div>
        </div>
      )}

      {/* News Detail Modal */}
      {selectedNews && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setSelectedNews(null)}>
          <div className="bg-white rounded-2xl p-6 w-[360px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <span className={`text-xs px-2 py-1 rounded font-medium ${
                selectedNews.type === "通知" ? "bg-orange-50 text-[#E8531A]" :
                selectedNews.type === "安全" ? "bg-red-50 text-red-600" :
                selectedNews.type === "活动" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"
              }`}>{selectedNews.type}</span>
              <button onClick={() => setSelectedNews(null)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">{selectedNews.title}</h3>
            <div className="text-xs text-gray-400 mb-4">{selectedNews.date}</div>
            <p className="text-sm text-gray-600 leading-relaxed">{selectedNews.content}</p>
            <button onClick={() => setSelectedNews(null)} className="w-full mt-5 bg-[#E8531A] text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap">我知道了</button>
          </div>
        </div>
      )}

      {/* Back button */}
      <button onClick={() => navigate("/")} className="fixed top-4 left-4 bg-white border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap shadow-sm">
        <i className="ri-arrow-left-line mr-1"></i>返回系统
      </button>
    </div>
  );
}
